import React, { useState } from 'react';
import { CreditCard, Package, ArrowRight } from 'lucide-react';

const StripeCheckout = ({ onPaymentInitiated }) => {
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Package definitions (must match backend)
  const packages = {
    basic: {
      name: 'Amaro Basic',
      price: 29.99,
      description: 'Single bottle custom amaro',
      features: ['1 Bottiglia Personalizzata', 'Selezione Botanici Base', 'Etichetta Standard']
    },
    premium: {
      name: 'Amaro Premium', 
      price: 54.99,
      description: 'Premium ingredients and packaging',
      features: ['1 Bottiglia Premium', 'Botanici di Qualità Superiore', 'Etichetta Personalizzata', 'Confezione Regalo']
    },
    deluxe: {
      name: 'Amaro Deluxe',
      price: 89.99, 
      description: 'Luxury experience with gift box',
      features: ['1 Bottiglia Luxury', 'Botanici Rari e Pregiati', 'Etichetta di Lusso', 'Confezione Regalo Premium', 'Certificato di Autenticità']
    }
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get current origin URL
      const originUrl = window.location.origin;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: selectedPackage,
          origin_url: originUrl,
          metadata: {
            source: 'web_checkout',
            package_type: selectedPackage
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
        if (onPaymentInitiated) {
          onPaymentInitiated(data.session_id, data.payment_id);
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setError(error.message);
      console.error('Payment error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Scegli il Tuo Pacchetto
        </h1>
        <p className="text-gray-600">
          Seleziona l'esperienza perfetta per il tuo amaro personalizzato
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {Object.entries(packages).map(([packageId, packageInfo]) => (
          <div
            key={packageId}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 ${
              selectedPackage === packageId
                ? 'border-purple-500 bg-purple-50 shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => setSelectedPackage(packageId)}
          >
            <div className="flex items-center justify-between mb-4">
              <Package className={`w-8 h-8 ${selectedPackage === packageId ? 'text-purple-600' : 'text-gray-400'}`} />
              <div className={`w-5 h-5 rounded-full border-2 ${
                selectedPackage === packageId 
                  ? 'bg-purple-500 border-purple-500' 
                  : 'border-gray-300'
              }`}>
                {selectedPackage === packageId && (
                  <div className="w-full h-full rounded-full bg-white scale-50"></div>
                )}
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {packageInfo.name}
            </h3>
            
            <div className="text-3xl font-bold text-purple-600 mb-3">
              €{packageInfo.price}
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              {packageInfo.description}
            </p>
            
            <ul className="space-y-2">
              {packageInfo.features.map((feature, index) => (
                <li key={index} className="flex items-start text-sm text-gray-700">
                  <ArrowRight className="w-4 h-4 text-purple-500 mt-0.5 mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Riepilogo Ordine
            </h3>
            <p className="text-gray-600 text-sm">
              {packages[selectedPackage].name} - {packages[selectedPackage].description}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              €{packages[selectedPackage].price}
            </p>
            <p className="text-sm text-gray-500">IVA inclusa</p>
          </div>
        </div>
        
        <button
          onClick={handlePayment}
          disabled={isLoading}
          className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : (
            <>
              <CreditCard className="w-5 h-5 mr-2" />
              Procedi al Pagamento
            </>
          )}
        </button>
        
        <p className="text-xs text-gray-500 text-center mt-3">
          Pagamento sicuro gestito da Stripe. I tuoi dati sono protetti.
        </p>
      </div>
    </div>
  );
};

export default StripeCheckout;